import { createContext, useContext } from 'react';
import type {
  IsoDate,
  PeriodPreset,
  PeriodQuery,
  ResolvedPeriod,
  ResolvedPreset,
  ViewState,
} from '@sl/core';

// What "the whole app follows the range" means in code (REQ-021 AC4).
//
// The date control owns the view state and puts it here; every screen under the shell reads it and asks for
// nothing else:
//
//   const { view, today, periodQuery } = usePeriod();
//   const summary = useQuery(queries.summary(account.id, periodQuery));   // === periodQueryOf(view, today)
//
// `view` is the whole `ViewState` (period AND list controls) because they share one URL and one parser —
// the lists (T-041) change `level` / `filters` / `sort` through `setView` and the period stays untouched.

export interface PeriodChoice {
  preset: PeriodPreset;
  /** Custom ranges only; ignored for a named preset. */
  since?: IsoDate | null;
  until?: IsoDate | null;
}

export interface PeriodApi {
  /** The view the URL carries, with the user's saved preferences filling anything it leaves out. */
  view: ViewState;
  /**
   * **The SESSION's calendar today — `AccountInfo.today`** (D-589), which is the account's day in the
   * account's zone on the server that answered, never `new Date()` and never the device's day. On a demo
   * account it is the injected `Clock`'s day (D-020), so `?demoNow=` still moves everything; on a live
   * dataset it is the PULL's day, because that is the day the numbers end on. Screens use this one value, so
   * the app can never disagree with its own date button.
   */
  today: IsoDate;
  /** `resolvePeriod` for `view` against `today` — the dates, the comparison dates, the length, "so far". */
  resolved: ResolvedPreset;
  /** What the period-keyed queries take: `periodQueryOf(view, today)`. */
  periodQuery: PeriodQuery;
  /** The preset's name: "Last 7 days". What the MENU row reads, always (REQ-021 AC6). */
  label: string;
  /**
   * **What the CLOSED chip's name slot reads** (REQ-021 AC6, D-704) — `label`, except on the four presets
   * whose name is a claim about which day or month ("Today", "Yesterday", "This month", "Last month") when
   * the served range is not the one the DEVICE's clock would give that name: then it is the served day or
   * month ("Sep 30", "September"). See `chipName.ts`; unreachable on a demo (D-626).
   */
  chipName: string;
  /**
   * The chip's name is the served day rather than the preset's, so "so far" means "still filling up when we
   * last heard from Meta" rather than REQ-283 AC3's "still coming in" (D-704). `null` the rest of the time.
   */
  soFarExplainer: string | null;
  /** The range ON SCREEN in the user's locale: "Sep 15 – 21" (REQ-261) — `served`'s dates where they differ. */
  dates: string;
  /** The comparison range: "Sep 8 – 14". Present whether or not the comparison is switched on. */
  compareDates: string;
  /**
   * `meta.period` — the server's echo — but ONLY when it differs from what was asked for (D-588 / D-589).
   * `null` the rest of the time, which is every demo session and nearly every live one; it goes non-null when
   * a link carries a range running past the data's last day and the server answers the range that exists.
   */
  served: ResolvedPeriod | null;
  /** The range that was ASKED for, when it is not the one being shown; `null` otherwise. */
  datesAsked: string | null;
  /** One line saying the shown range is not the asked one; `null` when they agree. */
  servedHint: string | null;
  /** REQ-021 AC5: "Dates in America/Chicago". */
  timeZoneNote: string;
  /** Replace the whole view (the lists' own controls go through here too). Writes the URL, `replace` by default. */
  setView: (
    next: ViewState | ((current: ViewState) => ViewState),
    options?: { push?: boolean },
  ) => void;
  /** Change only the period (REQ-021 AC1); the list controls survive. */
  setPeriod: (choice: PeriodChoice) => void;
  /** "Compare to previous period" (REQ-021 AC3). */
  setCompare: (on: boolean) => void;
  /** The saved-preferences read (REQ-021 AC4) — the control shows its own loading and error states. */
  preferences: { isPending: boolean; isError: boolean };
  /**
   * The range on show is provisional: the URL says nothing about a period and the saved one has not arrived.
   * The control draws a skeleton rather than a range the user never chose and would see change under them.
   */
  settling: boolean;
}

export const PeriodContext = createContext<PeriodApi | null>(null);

/** The range every screen follows. Needs `<PeriodProvider>`, which the shell mounts around every screen. */
export function usePeriod(): PeriodApi {
  const value = useContext(PeriodContext);
  if (!value) throw new Error('usePeriod() needs the <PeriodProvider> the AppShell mounts.');
  return value;
}
